<template>
  <SectionShell id="practice" num="10" kicker="The Loop">
    <template #title>Everything above<br />collapses into <span class="sig">one loop</span><br />you run forever.</template>
    <template #lede>
      <p>
        Feature by feature, portrait by portrait, the procedure never changes. It is
        four moves, and the fourth one sends you back to the second. Most beginners
        stop after the third pass; the drawings you admire went round eight or nine
        times.
      </p>
    </template>

    <div class="loopwrap">
      <figure class="loop card">
        <svg viewBox="0 0 460 460" class="loop__svg" role="img"
             aria-label="A four-step cycle: accurate line drawing, place the darks, blend, lift the lights, then repeat">
          <defs>
            <marker id="lp-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M0 0 L10 5 L0 10z" fill="#56e1f0" />
            </marker>
          </defs>
          <rect width="460" height="460" fill="#080b10" />

          <!-- the ring -->
          <circle cx="230" cy="230" r="150" fill="none" stroke="#1e2732" stroke-width="1" />
          <g v-for="(s, i) in loop" :key="'arc' + i">
            <path
              :d="arc(i)"
              fill="none"
              :stroke="hover === i ? '#56e1f0' : '#2c3743'"
              stroke-width="2"
              marker-end="url(#lp-arrow)"
              style="transition: stroke .3s"
            />
          </g>

          <!-- the nodes -->
          <g v-for="(s, i) in loop" :key="'n' + i" @mouseenter="hover = i" @focusin="hover = i" tabindex="0">
            <circle
              :cx="nodeX(i)" :cy="nodeY(i)" r="46"
              :fill="hover === i ? '#12212b' : '#0d141c'"
              :stroke="hover === i ? '#56e1f0' : '#2c3743'"
              stroke-width="1.2"
              style="transition: all .3s"
            />
            <text :x="nodeX(i)" :y="nodeY(i) - 8" text-anchor="middle"
                  font-family="'JetBrains Mono',monospace" font-size="9" letter-spacing="1.4"
                  :fill="hover === i ? '#56e1f0' : '#5d6875'">{{ i + 1 }}</text>
            <text :x="nodeX(i)" :y="nodeY(i) + 10" text-anchor="middle"
                  font-family="'Space Grotesk',sans-serif" font-size="15"
                  :fill="hover === i ? '#f4f6f8' : '#8b96a5'">{{ s.n }}</text>
          </g>

          <!-- centre -->
          <text x="230" y="222" text-anchor="middle" font-family="'Space Grotesk',sans-serif"
                font-size="20" fill="#d8dee6">go round</text>
          <text x="230" y="248" text-anchor="middle" font-family="'Space Grotesk',sans-serif"
                font-size="20" fill="#ffb45c">again</text>
          <text x="230" y="278" text-anchor="middle" font-family="'JetBrains Mono',monospace"
                font-size="8.5" letter-spacing="1.6" fill="#5d6875">STEPS 2–4, UNTIL IT READS</text>
        </svg>
        <figcaption class="mono">hover a node</figcaption>
      </figure>

      <div class="loop__side">
        <transition name="swap" mode="out-in">
          <div :key="hover">
            <span class="k mono">Move {{ hover + 1 }} — {{ loop[hover].n }}</span>
            <h3>{{ loop[hover].t }}</h3>
            <p>{{ loop[hover].p }}</p>
          </div>
        </transition>

        <div class="sched card">
          <h4 class="mono">A workable practice week</h4>
          <ul>
            <li><b>Mon–Fri, 20 minutes.</b> One feature only, from a magazine photograph. Five noses one week, five mouths the next.</li>
            <li><b>Saturday, 90 minutes.</b> A full portrait from a gridded reference, start to signature.</li>
            <li><b>Sunday, 10 minutes.</b> Put last week's work on the wall across the room and squint at it. Whatever falls apart at that distance is next week's drill.</li>
          </ul>
          <p class="sched__note">
            Steady beats heroic. Twenty focused minutes daily will take you further in a
            month than one four-hour session every other Sunday, because the thing you
            are training is perception, and perception consolidates overnight.
          </p>
        </div>
      </div>
    </div>

    <div class="checklist">
      <h3 class="checklist__t">Before you sign it — the eleven-point squint test</h3>
      <ol>
        <li v-for="(c, i) in checks" :key="i" v-reveal="i * 40">
          <span class="mono">{{ String(i + 1).padStart(2, '0') }}</span>
          <p v-html="c"></p>
        </li>
      </ol>
    </div>

    <div class="finale" v-reveal>
      <p class="finale__t">
        You do not need talent. You need a grid, five values, a paper stump,
        and the patience to go round the loop <span class="hl">one more time</span>
        than you feel like.
      </p>
      <p class="finale__s mono">Now go make it look real.</p>
    </div>
  </SectionShell>
</template>

<script setup>
import { ref } from 'vue'
import SectionShell from '../ui/SectionShell.vue'

const hover = ref(0)

const loop = [
  {
    n: 'Line',
    t: 'An accurate line drawing — and nothing else.',
    p: 'Grid it, copy the shapes box by box, check the proportion rules, then erase the grid. Do not start shading to "see how it goes". Every hour spent shading an inaccurate line drawing is an hour spent making a convincing picture of the wrong person.'
  },
  {
    n: 'Darks',
    t: 'Place the #1s and #2s as flat shapes.',
    p: 'Nostrils, the line between the lips, pupils, the shadow under the jaw, the shadow side of the face. Treat them as shapes with borders, exactly like the grid exercise. Resist detail — you are building the skeleton of the value structure.'
  },
  {
    n: 'Blend',
    t: 'Pull the darks into halftone with the stump.',
    p: 'Same direction you laid the pencil, following the form. Dirty tortillion for shadow zones, clean one anywhere near the light. Squint constantly: the moment it reads as a smooth graduation rather than a smudge, stop.'
  },
  {
    n: 'Lift',
    t: 'Take the lights back out — then darken again.',
    p: 'Kneaded eraser pointed for catchlights and the shine on a lip, razor-edged for strands of hair. Then reapply darks over the edges you just lifted. This back-and-forth is where a drawing stops looking like a photocopy and starts looking alive.'
  }
]

const checks = [
  'Squint at it from three metres. Do the big value masses still read, or does it flatten into grey?',
  'Are the eyes exactly halfway down the head?',
  'Is there one whole eye-width between the eyes?',
  'Do both eyes look at the same thing, and do both irises come from the same stencil hole?',
  'Is there exactly one catchlight per eye, in the same place in both?',
  'Is there a true #1 black somewhere on the page? Without one, everything reads washed out.',
  'Is there untouched #5 paper somewhere? Without it, everything reads muddy.',
  'Is there a rim of reflected light separating the form shadow from the cast shadow?',
  'Have you left a hard outline anywhere — around a lip, a nostril, a jaw? Soften it into an edge.',
  'Does the hair have a band of light, and does it cast a shadow onto the forehead?',
  'Turn the drawing upside down beside the reference. Errors you have stopped seeing will jump out immediately.'
]

/* geometry for the loop diagram */
const R = 150
const cx = 230
const cy = 230
const nodeX = (i) => cx + Math.cos((i * 90 - 90) * (Math.PI / 180)) * R
const nodeY = (i) => cy + Math.sin((i * 90 - 90) * (Math.PI / 180)) * R

function arc(i) {
  const a0 = (i * 90 - 90 + 22) * (Math.PI / 180)
  const a1 = (i * 90 - 90 + 68) * (Math.PI / 180)
  const x0 = cx + Math.cos(a0) * R
  const y0 = cy + Math.sin(a0) * R
  const x1 = cx + Math.cos(a1) * R
  const y1 = cy + Math.sin(a1) * R
  return `M${x0} ${y0} A${R} ${R} 0 0 1 ${x1} ${y1}`
}
</script>

<style scoped>
.loopwrap {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: clamp(24px, 3.5vw, 56px);
  align-items: start;
}
.loop { margin: 0; overflow: hidden; position: sticky; top: 92px; background: #080b10; }
.loop__svg { display: block; width: 100%; height: auto; }
.loop g[tabindex] { cursor: pointer; outline: none; }
.loop figcaption {
  border-top: 1px solid var(--edge-soft);
  padding: 11px 14px;
  font-size: 9px;
  color: var(--text-faint);
}

.k { color: var(--signal); font-size: 10px; display: block; margin-bottom: 14px; }
.loop__side h3 {
  font-size: clamp(21px, 1.7vw + 13px, 31px);
  font-weight: 400;
  letter-spacing: -0.025em;
  margin-bottom: 16px;
}
.loop__side > div > p { color: var(--text-dim); min-height: 140px; }

.sched { margin-top: 26px; padding: 24px 26px; }
.sched h4 { color: var(--light); font-size: 10px; font-weight: 400; margin-bottom: 16px; }
.sched ul { margin: 0 0 16px; padding-left: 18px; color: var(--text-dim); font-size: 14px; }
.sched li { margin-bottom: 10px; line-height: 1.6; }
.sched b { color: var(--v5); font-weight: 400; }
.sched__note { color: var(--text-faint); font-size: 13.5px; margin: 0; }

.checklist { margin-top: clamp(60px, 8vh, 110px); }
.checklist__t {
  font-size: clamp(22px, 2vw + 14px, 34px);
  font-weight: 400;
  letter-spacing: -0.025em;
  margin-bottom: 30px;
}
.checklist ol {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1px;
  background: var(--edge-soft);
  border: 1px solid var(--edge-soft);
}
.checklist li {
  display: flex;
  gap: 16px;
  padding: 20px 22px;
  background: rgba(8, 11, 17, 0.8);
  transition: background 0.3s;
}
.checklist li:hover { background: rgba(16, 24, 33, 0.9); }
.checklist span { color: var(--signal); font-size: 10px; flex: none; padding-top: 3px; }
.checklist p { margin: 0; color: var(--text-dim); font-size: 13.5px; line-height: 1.6; }

.finale {
  margin-top: clamp(60px, 9vh, 120px);
  padding: clamp(36px, 5vw, 66px) 0;
  border-top: 1px solid var(--edge-soft);
  text-align: center;
}
.finale__t {
  font-size: clamp(24px, 3vw + 12px, 50px);
  line-height: 1.22;
  letter-spacing: -0.03em;
  color: var(--v5);
  font-weight: 300;
  max-width: 24ch;
  margin: 0 auto 26px;
}
.finale__t .hl { color: var(--light); }
.finale__s { color: var(--signal); font-size: 11px; }

.swap-enter-active, .swap-leave-active { transition: opacity 0.28s, transform 0.28s; }
.swap-enter-from { opacity: 0; transform: translateY(10px); }
.swap-leave-to { opacity: 0; transform: translateY(-10px); }

@media (max-width: 980px) {
  .loopwrap { grid-template-columns: 1fr; }
  .loop { position: static; }
}
</style>
